import React from "react";
import { motion } from "framer-motion";
import * as L from "lucide-react";
import { useContent } from "@/lib/cms";
const { ArrowRight } = L;

const features = [
  {
    Icon: L.Megaphone,
    title: "Broadcast Campaigns",
    desc: "Send personalised offers to thousands without getting flagged. 98% open rates.",
    tone: "blue",
    span: "md:col-span-5",
  },
  {
    Icon: L.Bot,
    title: "No-code Chatbots",
    desc: "Drag-and-drop flow builder with AI replies, conditional logic and handoff.",
    tone: "green",
    span: "md:col-span-7",
  },
  {
    Icon: L.Users,
    title: "Shared Team Inbox",
    desc: "Assign chats, tag conversations, leave private notes and never miss a lead.",
    tone: "blue",
    span: "md:col-span-7",
  },
  {
    Icon: L.BarChart3,
    title: "Realtime Analytics",
    desc: "Track agent performance, campaign ROI and customer sentiment live.",
    tone: "green",
    span: "md:col-span-5",
  },
  {
    Icon: L.Workflow,
    title: "Automation Workflows",
    desc: "Trigger sequences from any event — order placed, cart abandoned, payment received.",
    tone: "blue",
    span: "md:col-span-4",
  },
  {
    Icon: L.MessagesSquare,
    title: "Rich Media Templates",
    desc: "Send images, videos, carousels, catalogues and interactive buttons.",
    tone: "green",
    span: "md:col-span-4",
  },
  {
    Icon: L.Shield,
    title: "Enterprise Security",
    desc: "End-to-end encryption, role-based access, SOC 2 & GDPR ready.",
    tone: "blue",
    span: "md:col-span-4",
  },
];

const toneMap = {
  blue: { bg: "bg-blue-50", text: "text-blue-700", ring: "ring-blue-100" },
  green: { bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-100" },
};

export const Features = () => {
  const { data } = useContent("features", {});
  const items = data.items && data.items.length
    ? data.items.map((it, i) => ({
        Icon: L[it.icon] || L.Sparkles,
        title: it.title,
        desc: it.desc,
        tone: features[i % features.length].tone,
        span: features[i % features.length].span,
      }))
    : features;
  return (
    <section id="features" className="py-20 sm:py-28 bg-white relative overflow-hidden">
      <div className="absolute inset-0 dot-grid opacity-40 pointer-events-none" aria-hidden="true" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[420px] w-[840px] bg-gradient-to-b from-emerald-50/40 to-transparent blur-3xl rounded-full pointer-events-none" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-emerald-600">All-in-one platform</p>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            Everything you need to grow on WhatsApp
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            From the first hello to the recurring purchase — Wappit covers your entire customer journey on the world&apos;s most-loved messaging app.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-4">
          {items.map(({ Icon, title, desc, tone, span }, i) => {
            const t = toneMap[tone];
            return (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.04 }}
                data-testid={`feature-card-${title.toLowerCase().replace(/\s+/g, "-")}`}
                className={`sm:col-span-1 ${span} group relative rounded-3xl border border-slate-200/70 bg-white p-6 sm:p-7 hover:shadow-xl hover:-translate-y-0.5 transition-all`}
              >
                <div className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl ${t.bg} ${t.text} ring-4 ${t.ring}`}>
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 text-xl font-semibold text-slate-900">{title}</h3>
                <p className="mt-2 text-slate-600 leading-relaxed text-[15px]">{desc}</p>
                <a
                  href="#pricing"
                  data-testid={`feature-learn-more-${title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-blue-700 hover:text-blue-800"
                >
                  Learn more <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                </a>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
